/* eslint-disable no-unused-vars */
/** @module Auth0PreLoginHandlerBase */
/* eslint-disable import/extensions */
import Auth0UtilsBrowser from '../../../utils/auth0-utils-browser.mjs';
import { resolveSsasBaseUrl } from '../../../utils/ssas-urls.mjs';
import Auth0WhiteLabelAssetHelper from '../auth0-white-label-helper.mst.mjs';
import Auth0ManagementApiBrowser from '../../../utils/auth0-management-api-browser.mjs';
import Auth0SupportedIdps from '../../../utils/auth0-supported-idps.mjs';
import Auth0WaitSpinner from '../../pages/auth0-wait-spinner-page-helper.mst.mjs';
import logger from '../../../utils/logging/auth0-logger.mjs';

/**
 * @abstract
 */
class Auth0PreLoginHandlerBase {
  constructor(options) {
    console.log(`Auth0PreLoginHandlerBase => ${JSON.stringify(options)}`);
    this._name = options.name;
    this._description = options.description;
    this._ssOptions = options.ssOptions;
    const resolved = Auth0UtilsBrowser.tenantOrDomainResolver(options.tenantOrDomain);
    this._tenant = resolved.tenant;
    this._clientId = options.clientId;
    this._idpContext = options.idpContext;
    this._whiteLabelOptions = options.whiteLabelOptions || {};
    this._captchaOptions = options.captchaOptions;

    this._whiteLabelHelper = new Auth0WhiteLabelAssetHelper({
      tenantOrDomain: this._tenant.id,
      ...this._whiteLabelOptions,
    });

    this._qs = Auth0UtilsBrowser.parseQueryString();
    this._flow = this._qs.get('flow');

    // Resolve appLoginUrl from multiple sources (priority order):
    // 1. Query string param (e.g., from forgot password link)
    // 2. ssOptions.appLoginUrl (from login helper config, set by EJS from URL params)
    // 3. session_token JWT payload (set by Auth0 actions — MFA, force-change-password)
    //    This is the key source for action-redirect flows where the consuming app's login
    //    URL is embedded in the signed JWT but never appears in the callback URL itself.
    // 4. ssOptions.redirectAuth0Uri (legacy, for backwards compat)
    // 5. Fallback URL with tenant context
    const qsAppLoginUrl = this._qs.get('appLoginUrl');
    const ssOptionsAppLoginUrl = this._ssOptions?.appLoginUrl !== this._ssOptions?.redirectUri
      ? this._ssOptions?.appLoginUrl
      : null;
    const sessionTokenAppLoginUrl = this._extractAppLoginUrlFromSessionToken(this._qs.get('session_token'));
    const legacyRedirectUri = this._ssOptions?.redirectAuth0Uri;
    this._appLoginUrl = qsAppLoginUrl
      || ssOptionsAppLoginUrl
      || sessionTokenAppLoginUrl
      || legacyRedirectUri
      || this._buildFallbackAppLoginUrl();

    console.log('[PreLoginHandlerBase] appLoginUrl resolved:', {
      fromQueryString: qsAppLoginUrl || '(not found)',
      fromSsOptions: ssOptionsAppLoginUrl || '(not found)',
      fromSessionToken: sessionTokenAppLoginUrl || '(not found)',
      fromLegacyRedirectUri: legacyRedirectUri || '(not found)',
      resolved: this._appLoginUrl,
    });
  }

  get name() {
    return this._name;
  }

  get description() {
    return this._description;
  }

  /**
   * @function
   * @description This function determines, based on app state whether or not this handler is to be chosen as the pre login handler. Only zero or one will be chosen (first one to return true here)
   * @returns {bool} Indicating whether or not this handler should run
   */
  async shouldHandle() {
    throw new Error('Auth0PostLoginHandlerBase is an abstract class. You need to extend it and implement this function there...');
  }

  /**
   * @function
   * @description The Run() function holds the entirety of the pre login logic for this handler. This function will return true if it has handled the login request or false if the login request should continue
   * @param {object} [viewOptions] Optional object used to propagate initialization options to any view that the handler may render
   * @returns {bool} Indicating whether or not the login request has been handled or should continue
   */
  async run(viewOptions = {}) {
    throw new Error('Auth0PostLoginHandlerBase is an abstract class. You need to extend it and implement this function there...');
  }

  /**
   * Build fallback app login URL with tenant context preserved
   * When no redirectAuth0Uri is provided, we need to construct a URL that includes
   * client ID for SSAS tenant resolution. Extract from URL params or grant token.
   * @returns {string} URL with tenant context
   */
  _buildFallbackAppLoginUrl() {
    const qs = Auth0UtilsBrowser.parseQueryString();
    const baseUrl = Auth0UtilsBrowser.locationWithNoSearchParams;

    // Try to find client ID from multiple sources for tenant context
    const clientId = qs.get('targetClientId')
      || qs.get('client_id')
      || this._clientId
      || this._extractClientIdFromGrantToken(qs.get('session_token'));

    if (clientId) {
      // Construct /loginauth URL with tenant context
      return `${window.location.origin}/loginauth?targetClientId=${encodeURIComponent(clientId)}`;
    }

    // Fallback to URL without search params (may cause tenant resolution error in SSAS)
    return baseUrl;
  }

  /**
   * Extract clientId from a grant token (JWT) if present
   * @param {string} token - JWT token that may contain clientId in payload
   * @returns {string|null} Client ID or null
   */
  _extractClientIdFromGrantToken(token) {
    if (!token) return null;
    try {
      const payload = Auth0UtilsBrowser.getJwtBodyFromToken(token);
      return payload?.clientId || null;
    } catch {
      return null;
    }
  }

  /**
   * Extract appLoginUrl from a session_token JWT issued by Auth0 actions (MFA, force-change-password).
   * The action encodes the consuming app's login URL in the token payload so it survives the
   * redirect to SSAS where no app context is available in the callback URL params.
   * @param {string} token - session_token JWT from query string
   * @returns {string|null} appLoginUrl or null
   */
  _extractAppLoginUrlFromSessionToken(token) {
    if (!token) return null;
    try {
      const payload = Auth0UtilsBrowser.getJwtBodyFromToken(token);
      return payload?.appLoginUrl || null;
    } catch {
      return null;
    }
  }

  get _socialIdpHelper() {
    if (this.__socialIdpHelper) {
      return this.__socialIdpHelper;
    }
    this.__socialIdpHelper = new Auth0SupportedIdps({
      tenantOrDomain: this._tenant.id,
      appLoginHandlerUrl: this._ssOptions.redirectAuth0Uri,
    });
    return this.__socialIdpHelper;
  }

  get _auth0ManagementApi() {
    if (!this._a0Api) {
      this._a0Api = new Auth0ManagementApiBrowser({
        tenantOrDomain: this._tenant.id,
        clientId: this._ssOptions.clientId,
        authServiceBaseUrl: resolveSsasBaseUrl('auth-stage', 'shipstation.com,sslocal.com', window.location.hostname),
      });
    }
    return this._a0Api;
  }

  _initLogger = (sourceName) => {
    logger.initialize({
      sumoEndpoint: 'https://endpoint2.collection.us2.sumologic.com/receiver/v1/http/ZaVnC4dhaV1uAI96duDvnKsBH-5zBCQS4Z9yRC6qyLXrEdfezmbhON_4so37Qor_6qM1eto00rRWjhlaqRccMgcVRvrLAKRfcyNBdeziv7B_grZ3H8sjew==',
      sourceName,
    });
  };

  _handledUnexpectedException = async (e) => {
    logger.error('Unexpected error in Auth0PreLoginHandlerBase', e);
    await logger.log(e);
    if (this.sentry) {
      this.sentry.captureException(e);
    }
  };

  async _showSpinner(options = {}) {
    await new Auth0WaitSpinner({
      name: options.parent || 'Auth0PreLoginHandlerBase',
      waitTextOverrideKey: options.waitTextOverrideKey,
      waitTextReplacementArgs: options.waitTextReplacementArgs,
      tenantOrDomain: this._tenant.id,
      whiteLabelOptions: this._whiteLabelOptions,
    }).render();
  }
}

export default Auth0PreLoginHandlerBase;
